import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import { useAuth } from '../providers/AuthProvider';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { ErrorMessage } from '../components/ErrorMessage';
import { listRecipes } from '../graphql/recipes';
import type { Recipe } from '../types/recipe';

const QUERY_TIMEOUT_MS = 10_000;

function truncateTitle(title: string): string {
  if (title.length > 80) {
    return title.slice(0, 80) + '\u2026';
  }
  return title;
}

function getPerServingByType(recipe: Recipe): Record<string, number> {
  const totals: Record<string, number> = {};

  for (const ingredient of recipe.ingredients) {
    if (ingredient.isCannabisInfused && ingredient.cannabinoidType && ingredient.materialWeight && ingredient.cannabinoidPercentage) {
      const decarb = ingredient.decarboxylationEfficiency ?? 0.88;
      const extraction = ingredient.extractionEfficiency ?? 0.70;
      const mg = ingredient.materialWeight * (ingredient.cannabinoidPercentage / 100) * 1000 * decarb * extraction;
      totals[ingredient.cannabinoidType] = (totals[ingredient.cannabinoidType] ?? 0) + mg;
    }
  }

  const perServing: Record<string, number> = {};
  for (const [type, totalMg] of Object.entries(totals)) {
    perServing[type] = Math.round((totalMg / recipe.servings) * 100) / 100;
  }

  return perServing;
}

export function RecipeListPage() {
  const { user } = useAuth();
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRecipes = useCallback(async () => {
    if (!user) return;

    setIsLoading(true);
    setError(null);

    try {
      const client = generateClient();
      const timeout = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('TIMEOUT')), QUERY_TIMEOUT_MS)
      );

      const request = client.graphql({
        query: listRecipes,
        variables: { userId: user.userId },
      });

      const response = await Promise.race([request, timeout]) as { data: { listRecipes: Recipe[] } };
      const fetched = response.data.listRecipes;

      // Sort by createdAt descending (most recent first)
      fetched.sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());

      setRecipes(fetched);
    } catch (err) {
      if (err instanceof Error && err.message === 'TIMEOUT') {
        setError('The request timed out. Please check your connection and try again.');
      } else {
        setError('Failed to load recipes. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchRecipes();
  }, [fetchRecipes]);

  if (isLoading) {
    return (
      <div className="page recipes-page">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="page recipes-page">
        <h2>My Recipes</h2>
        <ErrorMessage message={error} onRetry={fetchRecipes} />
      </div>
    );
  }

  if (recipes.length === 0) {
    return (
      <div className="page recipes-page">
        <h2>My Recipes</h2>
        <p className="page-description">
          Create and manage your infused recipes with automatic potency tracking.
        </p>
        <div className="empty-state">
          <p>No recipes yet. Create your first infused recipe to get started.</p>
          <Link to="/recipes/new" className="cta-button">Create Recipe</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page recipes-page">
      <h2>My Recipes</h2>
      <p className="page-description">
        Create and manage your infused recipes with automatic potency tracking.
      </p>
      <Link to="/recipes/new" className="cta-button" style={{ marginBottom: '1.5rem', display: 'inline-block' }}>
        Create Recipe
      </Link>
      <div className="recipe-list">
        {recipes.map((recipe) => {
          const perServing = getPerServingByType(recipe);
          return (
            <Link
              key={recipe.recipeId}
              to={`/recipes/${recipe.recipeId}`}
              className="recipe-card"
            >
              <h3 className="recipe-card-title">{truncateTitle(recipe.title)}</h3>
              <div className="recipe-card-meta">
                <span className="recipe-card-servings">{recipe.servings} serving{recipe.servings !== 1 ? 's' : ''}</span>
                {Object.keys(perServing).length > 0 && (
                  <span className="recipe-card-potency">
                    {Object.entries(perServing).map(([type, mg]) => (
                      <span key={type} className="potency-badge">
                        {mg} mg {type}/serving
                      </span>
                    ))}
                  </span>
                )}
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
